// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/components/admin/SchoolRolesAdminEditor.js
//
// Éditeur admin de la fonction d'un membre DANS l'établissement.
// Rendu uniquement dans les contextes où `isAdmin === true && orgType ===
// 'alumni'` — le composant parent doit faire ce gating avant de monter.
// (Le backend re-vérifie les deux : 403 sinon.)
//
// ── POURQUOI DES CHIPS ET PAS UN SELECT ────────────────────────────────────
// La taxonomie est FERMÉE et courte (8 clés). Des chips à bascule montrent
// l'ensemble des possibilités d'un coup d'œil, ce qu'un select à ouvrir ne
// fait pas, et rendent le cumul évident — or le cumul est la raison d'être de
// cet axe (un directeur adjoint est aussi enseignant).
//
// ── CE QUE CE COMPOSANT N'A PAS LE DROIT DE FAIRE ──────────────────────────
// Toucher au rôle technique. `school_roles` n'accorde AUCUN droit :
// school_roles=['direction'] + role='user' est un état valide et attendu.
// Ne JAMAIS ajouter ici un raccourci « passer aussi administrateur » : ce
// serait rejouer l'invariant member_type='professor' ⇒ role='admin' que cet
// axe existe précisément pour ne plus reproduire. Passer quelqu'un
// administrateur reste une action séparée et explicite.

import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CButton,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CAlert,
  CSpinner,
} from '@coreui/react'
import { put } from '../../utils/apiClient'
import {
  SCHOOL_ROLE_VALUES,
  SCHOOL_ROLE_BADGE_COLOR,
  getSchoolRoleLabel,
  sortSchoolRoles,
} from '../../utils/schoolRoles'

const SchoolRolesAdminEditor = ({ member, onUpdate, renderTrigger }) => {
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  // Toujours passer par sortSchoolRoles : l'ordre en base n'est pas fiable
  // (backfill SQL qui append vs route admin qui réordonne) et des clés
  // inconnues peuvent traîner sur d'anciennes lignes.
  const [selected, setSelected] = useState(() => sortSchoolRoles(member.school_roles))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const current = sortSchoolRoles(member.school_roles)
  const hasRoles = current.length > 0

  // Re-synchronise si la prop change sans remount (refetch parent). Sans ça,
  // ouvrir / modifier / fermer / rouvrir afficherait l'état local périmé.
  useEffect(() => {
    setSelected(sortSchoolRoles(member.school_roles))
  }, [member.id, member.school_roles])

  const openModal = () => {
    setSelected(sortSchoolRoles(member.school_roles))
    setError(null)
    setShowModal(true)
  }

  const closeModal = () => {
    if (loading) return // pas de fermeture pendant une requête
    setShowModal(false)
  }

  const toggleRole = (role) => {
    setSelected((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : sortSchoolRoles([...prev, role]),
    )
  }

  const handleSave = async () => {
    setLoading(true)
    setError(null)
    try {
      // Le backend re-sanitize (clés inconnues écartées, ordre canonique) et
      // renvoie ce qui est STOCKÉ : on propage sa réponse, pas notre state,
      // pour que l'affichage reflète la base et non l'intention.
      const res = await put(`/members/${member.id}/school-roles`, { school_roles: selected })
      if (onUpdate) onUpdate({ school_roles: res.school_roles })
      setShowModal(false)
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          t('dashboard:school_roles.error_generic'),
      )
    } finally {
      setLoading(false)
    }
  }

  const triggerLabel = hasRoles
    ? t('dashboard:school_roles.manage_button')
    : t('dashboard:school_roles.assign_button')

  // renderTrigger : même mécanisme que les autres actions admin de
  // MemberDetailsCard — permet de regrouper ce déclencheur dans un menu
  // déroulant. Défaut = CButton (comportement historique).
  const trigger = renderTrigger ? (
    renderTrigger({ onClick: openModal, label: triggerLabel })
  ) : (
    <CButton
      color={hasRoles ? SCHOOL_ROLE_BADGE_COLOR : 'light'}
      size="sm"
      onClick={openModal}
      title={t('dashboard:school_roles.manage_tooltip')}
      // z-index + position : le header de la fiche profil porte un overlay
      // décoratif qui rendrait le bouton non cliquable sans ça. Même fix que
      // les boutons voisins (ambassadeur, envoyer un message).
      style={{ zIndex: 10, position: 'relative' }}
    >
      {triggerLabel}
    </CButton>
  )

  return (
    <>
      {trigger}

      <CModal visible={showModal} onClose={closeModal} backdrop="static">
        <CModalHeader>
          <CModalTitle>{t('dashboard:school_roles.modal_title')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {error && (
            <CAlert color="danger" className="py-2 mb-3" style={{ fontSize: '0.9rem' }}>
              {error}
            </CAlert>
          )}
          <p className="text-medium-emphasis mb-3" style={{ fontSize: '0.9rem' }}>
            {t('dashboard:school_roles.intro', {
              name: `${member.firstName} ${member.lastName}`,
            })}
          </p>

          <div className="d-flex flex-wrap gap-2">
            {SCHOOL_ROLE_VALUES.map((role) => {
              const active = selected.includes(role)
              return (
                <CButton
                  key={role}
                  size="sm"
                  color={active ? SCHOOL_ROLE_BADGE_COLOR : 'secondary'}
                  variant={active ? undefined : 'outline'}
                  onClick={() => toggleRole(role)}
                  disabled={loading}
                  aria-pressed={active}
                >
                  {getSchoolRoleLabel(role, t)}
                </CButton>
              )
            })}
          </div>

          <small className="text-medium-emphasis d-block mt-3">
            {selected.length === 0
              ? t('dashboard:school_roles.empty_hint')
              : t('dashboard:school_roles.help')}
          </small>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={closeModal} disabled={loading}>
            {t('dashboard:school_roles.cancel')}
          </CButton>
          <CButton color={SCHOOL_ROLE_BADGE_COLOR} onClick={handleSave} disabled={loading}>
            {loading ? <CSpinner size="sm" /> : t('dashboard:school_roles.save')}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default SchoolRolesAdminEditor
